<template>
  <div :id="id">
    <div class="input">
      <select
        v-if="floModel !== 'other'"
        id="flo-serial-prefix"
        ref="prefixInput"
        v-model="serialPrefix"
        :data-vv-name="name"
        :disabled="readonly"
        :name="name"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleInput"
        @keydown.enter="handleEnterPress"
        :style="{ background: theme.primaryColor }"
      >
        <option v-for="option in prefixOptions" :key="option.value" :value="option.value">{{ $t(option.label) || $t(option.value) }}</option>
      </select>
      <input
        id="flo-serial-number"
        class="form-control"
        type="text"
        :style="{ color: theme.primaryColor }"
        :name="name"
        :readonly="readonly"
        v-model="serialNumber"
        :placeholder="floModel !== 'other' ? 'XXXXX' : ''"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="handleEnterPress"
        @input="handleInput"
        ref="numberInput"
      />
    </div>
    <p class="text-danger" v-if="floModel !== 'other' && !isValid && serialNumber.trim() !== ''">{{ errorStates }} Example: {{ serialPrefix }}-12345</p>
    <p class="text-danger" v-if="floModel === 'other' && serialNumber.trim() !== '' && !isValid">{{ errorStates }} Example: AAE-1234</p>
  </div>
</template>

<script>
import _ from 'lodash';
import defaultTheme from '../../styles/defaultTheme';

const floModelChars = {
  v1: 'E',
  v2: 'B',
};

export default {
  name: 'question-flo-serial',
  props: {
    id: String,
    name: String,
    readonly: Boolean,
    value: String,
    active: Boolean,
    stepValues: Object,
  },
  inject: {
    theme: { default: defaultTheme },
  },
  data() {
    return {
      serialPrefix: '',
      serialNumber: '',
    };
  },
  computed: {
    prefixOptions() {
      const floModelChar = floModelChars[_.get(this.stepValues, 'floModel')];
      return [
        { label: `AU${floModelChar}`, value: `AU${floModelChar}` },
        { label: `AA${floModelChar}`, value: `AA${floModelChar}` },
      ];
    },
    combinedValue() {
      if (this.floModel === 'other') return this.serialNumber.trim();
      return `${this.serialPrefix}-${this.serialNumber.trim()}`;
    },
    floModel() {
      return _.get(this.stepValues, 'floModel');
    },
    isCorrectLength() {
      return this.serialNumber.trim().length === 5;
    },
    isOtherCorrectLength() {
      return this.serialNumber.trim().length === 8;
    },
    isSerialNoDigits() {
      return /^[0-9]+$/.test(this.serialNumber);
    },
    isOtherFormatCorrect() {
      return /^[A-Za-z]{3}-\d{4}$/.test(this.serialNumber);
    },
    isValid() {
      if (this.floModel === 'other') return this.isOtherFormatCorrect && this.isOtherCorrectLength;
      return this.isCorrectLength && this.isSerialNoDigits;
    },
    errorStates() {
      const errorStates = [];
      if (this.floModel === 'other') {
        if (!this.isOtherFormatCorrect) errorStates.push('Format incorrect.');
        if (!this.isOtherCorrectLength) {
          if (this.serialNumber.trim().length < 8) errorStates.push('Value too short.');
          else errorStates.push('Value too long.');
        }
      } else {
        if (!this.isCorrectLength) {
          if (this.serialNumber.trim().length < 5) errorStates.push('Value too short.');
          else errorStates.push('Value too long.');
        }
        if (!this.isSerialNoDigits) errorStates.push('Value must be a number.');
      }
      return errorStates.join(' ');
    },
  },
  mounted() {
    if (this.active) {
      this.focus();
    }
    this.setSelect();
  },
  watch: {
    /**
     * If this question becomes active, focus the input
     */
    active(newActive, oldActive) {
      if (newActive && !oldActive) this.focus();
    },
    value(newValue, oldValue) {
      if (newValue !== oldValue && this.floModel !== 'other') {
        const serialValues = newValue.split('-');
        [this.serialPrefix, this.serialNumber] = serialValues;
      }
    },
    prefixOptions(newOpts, oldOpts) {
      if (newOpts.some((option, i) => option.value !== oldOpts[i].value)) {
        this.setSelect();
      }
    },
    floModel(newModel, oldModel) {
      if (newModel !== oldModel) {
        this.serialNumber = '';
        this.$emit('input', this.combinedValue);
      }
    },
    serialPrefix(newPrefix, oldPrefix) {
      if (newPrefix !== oldPrefix) {
        this.$emit('input', this.combinedValue);
      }
    },
  },
  methods: {
    focus() {
      this.$refs.numberInput.focus();
    },
    handleFocus() {
      this.$emit('focus');
    },
    handleEnterPress(e) {
      if (e.target.id === 'flo-serial-prefix') this.$refs.numberInput.focus();
      else this.$emit('keyPressEnter');
    },
    handleInput() {
      this.$emit('input', this.combinedValue);
    },
    handleBlur() {
      this.$emit('blur');
    },
    setSelect() {
      if (this.prefixOptions.length > 0 && this.prefixOptions[0].value) {
        this.serialPrefix = this.prefixOptions[0].value;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/styles.scss';

  div.input {
    display: flex;
    align-items: center;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    margin-top: 15px;
  }
  p.text-danger {
    font-size: 14px;
  }
  select {
    font-size: 25px;
    flex: 0 0 auto;
    margin: 0px;
    font-size: 1.2em;
    border: none;
    background: #FFFFFF;
    padding: 0.375rem 17px;;
    color: white;
    border-radius: 6px 0px 0px 6px;
  }
  input {
    flex: 0 1 auto;
    box-shadow: none !important;
    margin: 0px !important;
  }
</style>
